'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { createSecureClient } from '@polymarket/client';
import { signerFrom } from '@polymarket/client/viem';

import { useAuth } from '@/hooks/useAuth';
import { usePolymarketClient } from '@/hooks/usePolymarketClient';
import { clobApi } from '@/services/api/clob.api';
import { useAppDispatch } from '@/store';
import {
  setIsReadyToTrade,
  setProxyError,
  setSetupRequested,
  updateUser,
} from '@/store/slices/auth.slice';
import type { TradingWalletKind } from '@/types/auth.types';
import type { ClobApiCredentials } from '@/types/clob.types';
import { formatBuilderCode, normalizeClobAddress } from '@/utils/clob.utils';
import {
  clearStoredClobBinding,
  readStoredClobBinding,
  readStoredClobEoa,
  writeStoredClobBinding,
} from '@/utils/clobCredentialBindingStorage';

import { CLOB_API_URL, POLY_BUILDER_CODE, POLYGON_CHAIN_ID } from '@/constants/api.constants';

/**
 * useClobCredentials
 *
 * A high-stakes utility hook that manages the lifecycle of Polymarket CLOB API credentials.
 * These credentials are required for HMAC-authenticated requests (trading, order retrieval).
 *
 * Logic Flow:
 * 1. Analysis: Checks if credentials already exist in the Redux store or on the Porvenix backend.
 * 2. Signer Prep: L1 EIP-712 uses the user's EOA; for deposit wallets the signed `address` field is the deposit wallet.
 * 3. Generation: createApiKey/derive (or deposit-wallet L1 flow) produces credentials.
 * 4. Synchronization: Sinks the tripartite (Key, Secret, Passphrase) set to the backend for persistence.
 */
export function useClobCredentials() {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAuth();
  const { signer } = usePolymarketClient();

  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<ClobApiCredentials | null>(null);
  const [hasLocalCredentials, setHasLocalCredentials] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Addresses sourced from current session
  const eoaAddress = user?.walletAddress;
  const proxyWalletAddress = user?.proxyWalletAddress;
  const tradingWalletKind = user?.tradingWalletKind;

  // Prevents race conditions or double-signatures if multiple components trigger setup
  const hasProvisionedRef = useRef(false);
  const statusRequestRef = useRef<Promise<any> | null>(null);
  const lastStatusFetchAtRef = useRef(0);

  /**
   * Sync local state when the Redux store changes.
   */
  useEffect(() => {
    if (user?.id) {
      const stored = user?.clobCredentials;
      if (stored) {
        setCredentials(stored);
        setHasLocalCredentials(true);
      } else {
        setCredentials(null);
        setHasLocalCredentials(false);
      }
    }
  }, [user?.id, user?.clobCredentials]);

  /**
   * Restore `clobCredentialBinding` from localStorage (API does not return it) and drop
   * stale EOA-scoped CLOB keys for deposit-wallet users. Single effect avoids wiping
   * credentials before localStorage binding is applied (separate effects share one render snapshot).
   */
  useEffect(() => {
    if (!isAuthenticated || !user?.id || typeof window === 'undefined') return;

    const storedBinding = readStoredClobBinding(user.id);
    if (
      user.tradingWalletKind === 'deposit_wallet' &&
      storedBinding &&
      storedBinding !== 'deposit_wallet'
    ) {
      clearStoredClobBinding(user.id);
    }

    let effectiveBinding = user.clobCredentialBinding;
    if (!effectiveBinding && storedBinding) {
      if (user.tradingWalletKind !== 'deposit_wallet' || storedBinding === 'deposit_wallet') {
        effectiveBinding = storedBinding;
        if (user.clobCredentialBinding !== storedBinding) {
          dispatch(updateUser({ clobCredentialBinding: storedBinding }));
        }
      }
    }

    if (tradingWalletKind !== 'deposit_wallet' || !user.clobCredentials) return;
    if (effectiveBinding === 'deposit_wallet') return;

    void (async () => {
      if (user.id) clearStoredClobBinding(user.id);
      setCredentials(null);
      setHasLocalCredentials(false);
      dispatch(updateUser({ clobCredentials: null as any, clobCredentialBinding: undefined }));
      dispatch(setIsReadyToTrade(false));
      // Re-trigger setup so ProxyWalletInitializer re-runs credential provisioning.
      dispatch(setSetupRequested(true));
    })();
  }, [
    isAuthenticated,
    user?.id,
    user?.clobCredentials,
    user?.clobCredentialBinding,
    user?.tradingWalletKind,
    tradingWalletKind,
    dispatch,
  ]);

  /**
   * Refetches the credential status from the Porvenix backend.
   * Useful during initialization or after a wallet switch.
   */
  const refetchStatus = useCallback(async (): Promise<ClobApiCredentials | null> => {
    if (!isAuthenticated || !user?.id) return null;

    const now = Date.now();
    if (statusRequestRef.current) {
      const pending = await statusRequestRef.current.catch(() => null);
      return pending?.success && pending?.data?.hasCredentials ? pending.data.credentials : null;
    }
    // Avoid hammering status endpoint across rapid rerenders/triggers.
    if (now - lastStatusFetchAtRef.current < 3000) {
      return credentials;
    }

    setIsCheckingStatus(true);
    try {
      statusRequestRef.current = clobApi.getCredentialsStatus();
      const status = await statusRequestRef.current;
      lastStatusFetchAtRef.current = Date.now();
      if (status.success && status.data.hasCredentials && status.data.credentials) {
        const creds = status.data.credentials;
        const sameCreds =
          credentials?.apiKey === creds.apiKey &&
          credentials?.secret === creds.secret &&
          credentials?.passphrase === creds.passphrase;
        if (!sameCreds) {
          setCredentials(creds);
          setHasLocalCredentials(true);
          dispatch(updateUser({ clobCredentials: creds }));
        }
        return creds;
      }
      return null;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[ClobCredentials] Status fetch failed:', error);
      }
      return null;
    } finally {
      statusRequestRef.current = null;
      setIsCheckingStatus(false);
    }
  }, [isAuthenticated, user?.id, credentials, dispatch]);

  /**
   * provisionCredentials
   *
   * Orchestrates the creation of a new API Key set.
   * This involves an EIP-712 signature from the user.
   *
   * @param proxyAddressOverride - The Safe address to link the credentials to.
   */
  const provisionCredentials = useCallback(
    async (
      proxyAddressOverride?: string,
      options?: { forceCreateNewKey?: boolean; tradingWalletKind?: TradingWalletKind },
    ): Promise<ClobApiCredentials | null> => {
      const activeProxyAddress = normalizeClobAddress(
        (proxyAddressOverride || proxyWalletAddress) as string,
      );
      const forceCreateNewKey = options?.forceCreateNewKey === true;
      const effectiveKind: TradingWalletKind =
        options?.tradingWalletKind ?? tradingWalletKind ?? 'gnosis_safe';

      // Ensure we have the minimum requirements to start
      if (!eoaAddress || !activeProxyAddress || !signer) {
        const missing = !eoaAddress ? 'EOA' : !activeProxyAddress ? 'Proxy' : 'Signer';
        console.warn(`[ClobCredentials] Cannot provision. Missing ${missing}.`);
        return null;
      }

      // Guard against concurrent provisioning attempts
      if (isProvisioning) return credentials;

      setIsProvisioning(true);
      setProvisionError(null);
      hasProvisionedRef.current = true;

      try {
        // Step 1: Pre-check backend to avoid unnecessary signatures
        // Skip this check when explicitly forcing a brand new key after auth failures.
        if (!proxyAddressOverride && !forceCreateNewKey) {
          const existing = await refetchStatus();
          if (existing) {
            // Check localStorage directly in case the Redux hydration effect hasn't fired yet.
            const storedBinding =
              user?.clobCredentialBinding ?? (user?.id ? readStoredClobBinding(user.id) : null);

            const bindingOk =
              effectiveKind === 'deposit_wallet'
                ? storedBinding === 'deposit_wallet'
                : storedBinding !== 'deposit_wallet';

            // Also verify the stored credentials belong to the CURRENT EOA.
            // If the user cleared storage / switched wallets, the stored key was
            // provisioned for a different EOA and would cause:
            //   "the order signer address has to be the address of the API KEY"
            const storedEoa = user?.id ? readStoredClobEoa(user.id) : null;
            const eoaOk = !storedEoa || storedEoa === eoaAddress?.toLowerCase();

            if (bindingOk && eoaOk) {
              dispatch(updateUser({ clobCredentials: existing }));
              return existing;
            }

            await clobApi.deleteCredentials().catch(() => {});
            if (user?.id) clearStoredClobBinding(user.id);
            dispatch(
              updateUser({ clobCredentials: null as any, clobCredentialBinding: undefined }),
            );
          }
        }

        // Step 2: L1 signing — obtain API keys.
        //
        // The API key is ALWAYS registered against the EOA address (POLY_ADDRESS = EOA).
        // Polymarket's /auth/api-key endpoint is pure ECDSA: it verifies
        //   ecrecover(POLY_SIGNATURE) == POLY_ADDRESS
        // and rejects any mismatch. Passing depositWallet as POLY_ADDRESS would fail
        // because ecrecover returns the EOA signer, not the deposit wallet.
        //
        // For deposit wallet (POLY_1271) orders: after the API key is provisioned for the
        // EOA, Polymarket's CLOB establishes the deposit wallet relationship when
        // updateBalanceAllowance is called with signature_type=3. L2 headers always use
        // POLY_ADDRESS = EOA (the signer address); the CLOB derives the deposit wallet
        // deterministically from the EOA + signature_type. From that point onward, CLOB
        // validates POLY_1271 orders via ERC-1271 on the deposit wallet, recovers the
        // underlying EOA signer, and checks that EOA == api_key.owner. ✓
        //
        // For gnosis_safe: same EOA-based key, order signer = EOA, direct match. ✓
        // Provision L2 API credentials via the SDK. createSecureClient derives
        // (or creates) the API key from the signer automatically and exposes it
        // on `.credentials`, subsuming the old create-then-derive fallback.
        // POLY_1271 (deposit wallet) vs Gnosis-Safe is auto-detected from the
        // `wallet` address, replacing the explicit signatureType/funderAddress.
        // The API key remains registered against the EOA signer (see note above).
        const provisioningClient = await createSecureClient({
          signer: signerFrom(signer),
          wallet: activeProxyAddress,
        });

        const derivedCreds = provisioningClient.credentials;
        if (!derivedCreds?.key) {
          throw new Error('Polymarket SDK failed to generate or derive a valid API key.');
        }

        const newCredentials: ClobApiCredentials = {
          apiKey: derivedCreds.key,
          secret: derivedCreds.secret,
          passphrase: derivedCreds.passphrase,
        };

        // Step 4: Register these keys on Porvenix backend for future session persistence
        const provisionResponse = await clobApi.provisionCredentials(newCredentials);
        if (!provisionResponse.success) {
          throw new Error('Backend refused to store the new credentials.');
        }

        // Step 5: Update all local listeners and persist to Redux
        setCredentials(newCredentials);
        setHasLocalCredentials(true);
        dispatch(
          updateUser({
            clobCredentials: newCredentials,
            clobCredentialBinding:
              effectiveKind === 'deposit_wallet' ? 'deposit_wallet' : 'gnosis_safe',
          }),
        );
        if (user?.id) {
          writeStoredClobBinding(
            user.id,
            effectiveKind === 'deposit_wallet' ? 'deposit_wallet' : 'gnosis_safe',
            eoaAddress, // persist EOA so stale keys from a different wallet are detected
          );
        }

        // Read-after-write check
        await refetchStatus().catch(() => null);

        // toast.success('Trading credentials ready!');
        return newCredentials;
      } catch (error: any) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[ClobCredentials] Provisioning failed:', error);
        }

        let msg = error?.message ?? 'Failed to provision credentials.';

        // Map common errors for better UX
        if (msg.toLowerCase().includes('user rejected') || error?.code === 4001) {
          msg = 'Transaction rejected. Trading keys were not created.';
        } else if (msg.toLowerCase().includes('failed to fetch')) {
          msg = 'Network connection to Polymarket timed out.';
        }

        setProvisionError(msg);
        dispatch(setProxyError(msg));
        toast.error(msg);

        hasProvisionedRef.current = false;
        return null;
      } finally {
        setIsProvisioning(false);
      }
    },
    [
      eoaAddress,
      proxyWalletAddress,
      tradingWalletKind,
      user?.clobCredentialBinding,
      user?.id,
      signer,
      isProvisioning,
      credentials,
      dispatch,
      refetchStatus,
    ],
  );

  // Auto-recovery: If we have credentials but the last trade failed with 401,
  // we should probably trigger a re-provisioning check.
  useEffect(() => {
    if (credentials && !isProvisioning) {
      // We could add a lightweight validation call here if needed
    }
  }, [credentials, proxyWalletAddress, isProvisioning]);

  /**
   * Clears the CLOB credentials from both the backend and local state.
   * Useful for recovery from 401 Unauthorized errors after migration.
   */
  const resetCredentials = useCallback(async () => {
    try {
      await clobApi.deleteCredentials().catch(() => {});
    } finally {
      // ALWAYS clear local state to allow re-provisioning.
      setCredentials(null);
      setHasLocalCredentials(false);
      if (user?.id) clearStoredClobBinding(user.id);
      dispatch(updateUser({ clobCredentials: null as any, clobCredentialBinding: undefined }));
      // toast.success('Trading credentials reset locally. You can now re-initialize.');
    }
  }, [dispatch, user?.id]);

  return {
    credentials,
    hasCredentials: hasLocalCredentials,
    isProvisioning,
    isCheckingStatus,
    provisionError,
    provisionCredentials,
    resetCredentials,
    refetchStatus,
    eoaAddress,
    proxyWalletAddress,
  };
}

export default useClobCredentials;
